/**
 * Lane CQ-248 (wave 12g, census lane V7; the v1.0.0 ruling of 2026-10-11) — the ONE reading of a version on the console.
 *
 * A production release is a git tag `vMAJOR.MINOR.PATCH` (lane CQ-178 tags it and stamps it into every build); a build without a
 * tag — DEV, a lane, every release before v1.0.0 — reads `v0.0.0-dev+<commit>` on the wire (SatoriShared BuildAttestation.Version,
 * `/healthz` `build.version`, `/health/services` `release.version`).  A person reads "version 1.0.0" for a release and "build
 * 1686aa9bbbc3" for anything else — never `v0.0.0-dev+…`, never a tag with its `v` in a sentence.  Pure; pinned by
 * releases.cq248.test.js.  Consumers (§4.16): systemHealthModel.js (the Running version figure, the answer line),
 * releasesModel.js (the answer line, the release history), deploymentsRunningNow.js (the Running now sentences), buildInfo.js
 * (the console's own bundle).
 *
 * Lane CQ-258 (F-252-1, 2026-10-15) — and the ONE reading of "which build do the services run": servingRelease() derives it from the
 * very rows Running now lists, so the Releases answer line, System health's Running version figure and the list can never disagree
 * (the gate saw "No service says which build it runs yet" above seven services listed on one build — the answer had read the
 * server's summary block while the list read each service's own build).
 */

/** A release tag — the same rule as SatoriShared BuildAttestation.IsReleaseTag and Directory.Build.props. */
export const RELEASE_TAG = /^v(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;

/** The version a build without a release tag reports (`v0.0.0-dev+<short commit>`, or this alone when the commit is unknown). */
export const DEV_VERSION_PREFIX = 'v0.0.0-dev';

export function isReleaseTag(value) {
  return typeof value === 'string' && RELEASE_TAG.test(value.trim());
}

/**
 * What a person calls a build: `{ kind: 'release', text: '1.0.0', tag: 'v1.0.0' }` for a release, `{ kind: 'build', text:
 * '<12-char commit>' }` for anything else that names its commit, `null` when nothing is known.  `build` = a `/health/services`
 * build block (`{ version?, commit?, shortCommit? }`) or a release block (`{ version?, commit, shortCommit }`).
 */
export function versionOf(build) {
  if (!build) return null;
  const v = typeof build.version === 'string' ? build.version.trim() : '';
  if (isReleaseTag(v)) return { kind: 'release', text: v.slice(1), tag: v };
  const short = build.shortCommit || (typeof build.commit === 'string' && build.commit.length >= 7 ? build.commit.slice(0, 12) : '');
  if (short && short !== 'unknown') return { kind: 'build', text: short };
  return null;
}

/** "version 1.0.0" · "build 1686aa9bbbc3" · "an unknown build" — for a sentence. */
export function versionWords(t, build) {
  const v = versionOf(build);
  if (!v) return t('releases.version.unknown');
  return v.kind === 'release' ? t('releases.version.release', { version: v.text }) : t('releases.version.build', { build: v.text });
}

/**
 * The build most services serve, from the service rows themselves (Running now's rows or System health's services):
 * `{ version, commit, shortCommit, builtAtUtc, serving, attesting, otherServices[] }`, or null when no service says what it runs.
 * A service "says what it runs" when it answers (not down, not unknown) with a build that names its commit; the admin console's own
 * row is the bundle this page was built from, never a service of the stack.  Ties go to the build listed first.  Pure.
 */
export function servingRelease(rows) {
  const attesting = (Array.isArray(rows) ? rows : [])
    .filter((r) => r && r.key !== 'admin' && r.state !== 'down' && r.state !== 'unknown' && r.build && versionOf(r.build)?.kind);
  if (attesting.length === 0) return null;
  const groups = [];
  for (const r of attesting) {
    const g = groups.find((x) => sameBuild(x.build, r.build));
    if (g) g.rows.push(r); else groups.push({ build: r.build, rows: [r] });
  }
  const top = groups.reduce((best, g) => (g.rows.length > best.rows.length ? g : best), groups[0]);
  return {
    version: top.build.version || null,
    commit: top.build.commit || null,
    shortCommit: top.build.shortCommit || (top.build.commit ? String(top.build.commit).slice(0, 12) : null),
    builtAtUtc: top.rows.map((r) => r.build.builtAtUtc).find(Boolean) || null,
    serving: top.rows.length,
    attesting: attesting.length,
    otherServices: attesting.filter((r) => !top.rows.includes(r)).map((r) => r.label || r.key),
  };
}

/** True when two build blocks name the same build (the release tag when both carry one, else the commit). */
export function sameBuild(a, b) {
  if (!a || !b) return false;
  const ta = isReleaseTag(a.version) ? a.version : null;
  const tb = isReleaseTag(b.version) ? b.version : null;
  if (ta && tb) return ta === tb;
  const ca = String(a.commit || '').toLowerCase();
  const cb = String(b.commit || '').toLowerCase();
  return Boolean(ca && cb && (ca.startsWith(cb) || cb.startsWith(ca)));
}
